import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ContractNote,
  PaymentStatus,
  createContractPaymentNote,
  getPaymentStatuses,
} from '../services/api';
import { formatDateTimeMsk } from '../utils/table';
import './ContractPaymentNotesChat.css';

interface ContractPaymentNotesChatProps {
  contractId?: number;
  /** При указании создаёт одинаковую запись для каждого договора из списка */
  contractIds?: number[];
  notes: ContractNote[];
  canEdit: boolean;
  onNoteAdded: (note: ContractNote) => void;
  variant?: 'inline' | 'drawer';
  /** Если задан, у комментария с одного договора показывается его номер */
  contractNumberById?: Record<number, string>;
}

function resolveDefaultStatusId(notes: ContractNote[]): number {
  const latestWithStatus = [...notes]
    .sort((a, b) => {
      const at = new Date(a.created_at).getTime();
      const bt = new Date(b.created_at).getTime();
      if (at !== bt) return bt - at;
      return b.id - a.id;
    })
    .find((note) => note.payment_status_id != null);
  return latestWithStatus?.payment_status_id ?? 0;
}

function resolveStatusLabel(
  note: ContractNote,
  statusTextById: Map<number, string>,
): string | null {
  const fromNote = note.payment_status_text?.trim();
  if (fromNote) return fromNote;
  if (note.payment_status_id != null) {
    return statusTextById.get(note.payment_status_id) ?? null;
  }
  return null;
}

function formatNoteBody(note: ContractNote, statusTextById: Map<number, string>): string {
  const statusLabel = resolveStatusLabel(note, statusTextById);
  if (statusLabel) {
    return `(${statusLabel}) ${note.note}`;
  }
  return note.note;
}

function noteFingerprint(note: ContractNote): string {
  return `${note.note}|${note.payment_status_id ?? ''}|${note.created_by_id ?? ''}`;
}

function dedupePaymentNotes(notes: ContractNote[]): ContractNote[] {
  const seen = new Set<string>();
  return notes.filter((note) => {
    const key = `${noteFingerprint(note)}|${note.created_at}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

interface DisplayNote extends ContractNote {
  contractNumber?: string;
}

const CONTRACT_BLAST_WINDOW_MS = 30_000;

function prepareDisplayNotes(
  notes: ContractNote[],
  contractNumberById?: Record<number, string>,
): DisplayNote[] {
  if (contractNumberById == null) {
    return dedupePaymentNotes(notes);
  }

  const sorted = [...notes].sort((a, b) => a.id - b.id);
  const used = new Set<number>();
  const clusters: ContractNote[][] = [];

  for (const note of sorted) {
    if (used.has(note.id)) continue;
    const cluster = [note];
    used.add(note.id);
    const t0 = new Date(note.created_at).getTime();
    const fingerprint = noteFingerprint(note);
    for (const other of sorted) {
      if (used.has(other.id)) continue;
      if (noteFingerprint(other) !== fingerprint) continue;
      const dt = Math.abs(new Date(other.created_at).getTime() - t0);
      if (dt > CONTRACT_BLAST_WINDOW_MS) continue;
      cluster.push(other);
      used.add(other.id);
    }
    clusters.push(cluster);
  }

  return clusters.map((cluster) => {
    const representative = cluster.reduce((best, item) => (item.id < best.id ? item : best));
    const contractIds = new Set(cluster.map((item) => item.contract_id));
    const contractNumber =
      contractIds.size === 1 ? contractNumberById[representative.contract_id] : undefined;
    return contractNumber
      ? { ...representative, contractNumber }
      : representative;
  });
}

const ContractPaymentNotesChat: React.FC<ContractPaymentNotesChatProps> = ({
  contractId,
  contractIds,
  notes,
  canEdit,
  onNoteAdded,
  variant = 'inline',
  contractNumberById,
}) => {
  const targetContractIds = useMemo(
    () => (contractIds != null && contractIds.length > 0 ? contractIds : contractId != null ? [contractId] : []),
    [contractId, contractIds],
  );
  const [paymentStatuses, setPaymentStatuses] = useState<PaymentStatus[]>([]);
  const [draft, setDraft] = useState('');
  const [statusDraftId, setStatusDraftId] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const historyRef = useRef<HTMLDivElement>(null);

  const defaultStatusId = useMemo(() => resolveDefaultStatusId(notes), [notes]);

  const statusOptions = useMemo(() => {
    const byId = new Map(paymentStatuses.map((status) => [status.id, status]));
    if (!byId.has(0)) {
      byId.set(0, { id: 0, text: '—' });
    }
    return [...byId.values()].sort((a, b) => a.id - b.id);
  }, [paymentStatuses]);

  const statusTextById = useMemo(
    () => new Map(statusOptions.map((status) => [status.id, status.text])),
    [statusOptions],
  );

  useEffect(() => {
    let cancelled = false;
    void getPaymentStatuses()
      .then((rows) => {
        if (!cancelled) {
          setPaymentStatuses(rows);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPaymentStatuses([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setStatusDraftId(defaultStatusId);
  }, [defaultStatusId]);

  const sortedNotes = useMemo(
    () =>
      prepareDisplayNotes(notes, contractNumberById).sort((a, b) => {
        const at = new Date(a.created_at).getTime();
        const bt = new Date(b.created_at).getTime();
        if (at !== bt) return at - bt;
        return a.id - b.id;
      }),
    [notes, contractNumberById],
  );

  useEffect(() => {
    const node = historyRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [sortedNotes.length]);

  const handleSubmit = async (): Promise<void> => {
    const trimmed = draft.trim();
    if (!trimmed || saving || targetContractIds.length === 0) return;
    setSaving(true);
    setError('');
    try {
      for (const id of targetContractIds) {
        const created = await createContractPaymentNote(id, trimmed, statusDraftId);
        onNoteAdded(created);
      }
      setDraft('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось добавить комментарий');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void handleSubmit();
    }
  };

  const isDrawer = variant === 'drawer';

  return (
    <div className={`contract-payment-notes-chat${isDrawer ? ' contract-payment-notes-chat--drawer' : ''}`}>
      {isDrawer ? null : (
        <div className="contract-payment-notes-chat__head">Комментарии по оплатам</div>
      )}
      <div ref={historyRef} className="contract-payment-notes-chat__history">
        {sortedNotes.length === 0 ? (
          <p className="contract-payment-notes-chat__empty">Комментариев пока нет</p>
        ) : (
          sortedNotes.map((item) => (
            <article key={item.id} className="contract-payment-notes-chat__message">
              <header className="contract-payment-notes-chat__message-head">
                <span className="contract-payment-notes-chat__author">
                  {item.created_by_name?.trim() || '—'}
                </span>
                <span className="contract-payment-notes-chat__time">
                  {formatDateTimeMsk(item.created_at)}
                </span>
              </header>
              <div className="contract-payment-notes-chat__text">
                {item.contractNumber ? (
                  <span className="contract-payment-notes-chat__contract">{item.contractNumber}</span>
                ) : null}
                {formatNoteBody(item, statusTextById)}
              </div>
            </article>
          ))
        )}
      </div>
      {canEdit ? (
        <div className="contract-payment-notes-chat__composer">
          <textarea
            className="contract-payment-notes-chat__input"
            rows={2}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (error) setError('');
            }}
            onKeyDown={handleKeyDown}
            disabled={saving}
            placeholder="Новый комментарий…"
            aria-label="Новый комментарий по оплатам"
          />
          <div className="contract-payment-notes-chat__composer-row">
            <select
              className="contract-payment-notes-chat__status-select"
              value={String(statusDraftId)}
              onChange={(e) => setStatusDraftId(Number(e.target.value))}
              disabled={saving}
              aria-label="Статус оплаты"
            >
              {statusOptions.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.text}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="submit-button contract-payment-notes-chat__submit"
              disabled={saving || !draft.trim()}
              onClick={() => void handleSubmit()}
            >
              {saving ? '…' : 'Отправить'}
            </button>
          </div>
          {error ? <div className="contract-payment-notes-chat__error">{error}</div> : null}
        </div>
      ) : null}
    </div>
  );
};

export default ContractPaymentNotesChat;
